<template>
  <template v-if="isAccountLoading">
    <Loading />
  </template>
  <template v-else>
    <div>
      <h2 class="pb-5">Setup payment account</h2>
      <p class="py-2">
        Please enter the details of the bank account which you would like your scheduled payments to
        be transferred to
      </p>

      <div class="flex flex-col py-2">
        <label for="accountType" class="block py-1">
          Account Type
          <span class="text-brand-red">*</span>
        </label>
        <div @click="handleEdit">
          <DropDown
            :options="accountTypeOptions"
            @select="selectAccountType"
            :placeholder="
              accountType
                ? accountTypeOptions.find((o) => o.value === accountType).label
                : 'Select account type'
            "
            :disabled="mode === 'edit'"
          />
        </div>
      </div>

      <template v-if="accountType === 'company'">
        <div class="flex flex-col py-2">
          <label for="businessName" class="block">
            Business name
            <span class="text-brand-red">*</span>
          </label>
          <div @click="handleEdit">
            <SingleLineInput
              id="businessName"
              type="text"
              :value="businessName"
              @input="businessName = $event.target.value"
              placeholder="Enter your business name"
              :disabled="mode === 'edit'"
            />
          </div>
        </div>
        <div class="flex flex-col py-2">
          <label for="taxId" class="block">
            EIN / Tax ID
            <span class="text-brand-red">*</span>
          </label>
          <div @click="handleEdit">
            <SingleLineInput
              id="taxId"
              type="text"
              :value="taxId"
              @input="taxId = $event.target.value"
              placeholder="Enter your business EIN / Tax ID"
              :disabled="mode === 'edit'"
            />
          </div>
        </div>
      </template>

      <div class="flex flex-col py-2">
        <label for="firstName" class="block">
          First name
          <span class="text-brand-red">*</span>
        </label>
        <div @click="handleEdit">
          <SingleLineInput
            id="firstName"
            type="text"
            :value="firstName"
            @input="firstName = $event.target.value"
            placeholder="Enter your first name"
            :disabled="mode === 'edit'"
          />
        </div>
      </div>

      <div class="flex flex-col py-2">
        <label for="firstName" class="block">
          Last name
          <span class="text-brand-red">*</span>
        </label>
        <div @click="handleEdit">
          <SingleLineInput
            id="lastName"
            type="text"
            :value="lastName"
            @input="lastName = $event.target.value"
            placeholder="Enter your last name"
            :disabled="mode === 'edit'"
          />
        </div>
      </div>

      <div class="flex flex-col py-2">
        <label for="dob" class="block">
          Date of birth
          <span class="text-brand-red">*</span>
        </label>
        <div @click="handleEdit">
          <SingleLineInput
            id="dob"
            type="text"
            :value="dob"
            @input="dob = $event.target.value"
            placeholder="MM/DD/YYYY"
            mask="date"
            :disabled="mode === 'edit'"
          />
        </div>
      </div>

      <div class="flex flex-col py-1">
        <label for="ssn" class="block">
          SSN
          <span class="text-brand-red" v-if="accountType === 'individual'">*</span>
        </label>
        <div @click="handleEdit">
          <SsnInput :ssn="ssn" @update="ssn = $event" :disabled="ssnFieldDisabled" />
        </div>
        <!-- <SingleLineInput
          id="ssn"
          type="text"
          :value="ssn"
          @input="ssn = $event.target.value"
          placeholder="XXX-XX-XXXX"
          mask="ssn"
          :disabled="ssnFieldDisabled"
        ></SingleLineInput> -->
        <p class="text-xs text-brand-mid-gray" v-if="accountType === 'company'">
          This is optional now, but due to federal anti money laundering and financial compliance
          laws, some accounts may be required to provide further information after certain monetary
          thresholds are reached.
          <a
            target="_blank"
            class="text-brand-green underline"
            href="https://stripe.com/docs/connect/required-verification-information#additional-verification-requirements-for-united-states"
            >See details</a
          >
        </p>
      </div>

      <div class="flex flex-col py-2">
        <label for="accountNumber" class="block">
          Account Number
          <span class="text-brand-red">*</span>
        </label>
        <SingleLineInput
          id="accountNumber"
          type="text"
          :value="accountNumber"
          @input="accountNumber = $event.target.value"
          placeholder="Enter your account number"
          mask="number"
        ></SingleLineInput>
      </div>

      <div class="flex flex-col py-2">
        <label for="routingNumber" class="block">
          Routing Number
          <span class="text-brand-red">*</span>
        </label>
        <SingleLineInput
          id="routingNumber"
          type="text"
          :value="routingNumber"
          @input="routingNumber = $event.target.value"
          placeholder="Enter your routing number"
          mask="number"
        ></SingleLineInput>
      </div>

      <template v-if="accountType === 'company'">
        <h5 class="text-xl font-semibold py-3 md:pt-5">Business Address</h5>

        <div class="flex flex-col py-2">
          <label for="companyAddressLine1" class="block">
            Address Line 1
            <span class="text-brand-red">*</span>
          </label>
          <div @click="handleEdit">
            <SingleLineInput
              id="companyAddressLine1"
              type="text"
              :value="companyAddressLine1"
              @input="companyAddressLine1 = $event.target.value"
              placeholder="123 Business Rd."
              :disabled="mode === 'edit'"
            />
          </div>
        </div>

        <div class="flex flex-col py-2">
          <label for="companyAddressLine2" class="block">
            Address Line 2
            <span class="text-brand-red">*</span>
          </label>
          <div @click="handleEdit">
            <SingleLineInput
              id="companyAddressLine2"
              type="text"
              :value="companyAddressLine2"
              @input="companyAddressLine2 = $event.target.value"
              placeholder="Unit #"
              :disabled="mode === 'edit'"
            />
          </div>
        </div>

        <div class="flex flex-col py-2">
          <label for="companyAddressCity" class="block">
            City
            <span class="text-brand-red">*</span>
          </label>
          <div @click="handleEdit">
            <SingleLineInput
              id="companyAddressCity"
              type="text"
              :value="companyAddressCity"
              @input="companyAddressCity = $event.target.value"
              :disabled="mode === 'edit'"
            />
          </div>
        </div>

        <div class="flex flex-col py-2">
          <label for="companyAddressState" class="block">
            State
            <span class="text-brand-red">*</span>
          </label>
          <div class="py-2" @click="handleEdit">
            <DropDown
              :options="stateOptions"
              @select="selectState"
              :placeholder="companyAddressState ? companyAddressState : 'Select state'"
              :disabled="mode === 'edit'"
            />
          </div>
        </div>

        <div class="flex flex-col py-2">
          <label for="companyAddressZip" class="block">
            Zip Code
            <span class="text-brand-red">*</span>
          </label>
          <div @click="handleEdit">
            <SingleLineInput
              id="companyAddressZip"
              type="text"
              :value="companyAddressZip"
              @input="companyAddressZip = $event.target.value"
              mask="zipcode"
              :disabled="mode === 'edit'"
            />
          </div>
        </div>
      </template>

      <div class="flex flex-col py-5">
        <p v-show="errorMessage" class="text-brand-red pb-2 text-center">
          {{ errorMessage }}
        </p>
        <div class="w-full md:w-1/3 md:mx-auto">
          <PrimaryButton @click="next">Next</PrimaryButton>
        </div>
        <p
          class="pt-10 text-center text-sm text-brand-green cursor-pointer hover:underline"
          @click="toggleModal"
        >
          Why we are asking for this information
        </p>
      </div>
    </div>
  </template>
  <WhyModal :showModal="showModal" @close="toggleModal" />
  <EditModal :showModal="showEditModal" @close="toggleEditModal" />
</template>
<script>
import hotkeys from "hotkeys-js"
import { mapGetters } from "vuex"
import states from "@/constants/states"
import { USER_SET_ACCOUNT_INFO, ACCOUNT_GET } from "@/store/types"
import WhyModal from "./components/WhyModal.vue"
import EditModal from "./components/EditModal.vue"

export default {
  components: {
    WhyModal,
    EditModal
  },
  computed: {
    ...mapGetters(["getCurrentAccount", "isAccountLoading"]),
    isValid() {
      if (this.mode === "edit") {
        return !!this.accountNumber && !!this.routingNumber
      }
      if (this.accountType === "company") {
        return (
          !!this.businessName &&
          !!this.taxId &&
          !!this.firstName &&
          !!this.lastName &&
          !!this.dob &&
          !!this.accountNumber &&
          !!this.routingNumber &&
          !!this.companyAddressLine1 &&
          !!this.companyAddressLine2 &&
          !!this.companyAddressCity &&
          !!this.companyAddressState &&
          !!this.companyAddressZip
        )
      } else if (this.accountType === "individual") {
        return (
          !!this.firstName &&
          !!this.lastName &&
          !!this.dob &&
          !!this.ssn &&
          !!this.accountNumber &&
          !!this.routingNumber
        )
      }
      return false
    },
    ssnFieldDisabled() {
      if (this.mode !== "edit") {
        return false
      }

      if (this.company && this.person && !this.person.ssn_last_4_provided) {
        return false
      }

      return true
    }
  },
  data() {
    return {
      showModal: false,
      showEditModal: false,
      accountType: "",
      businessName: "",
      taxId: "",
      firstName: "",
      lastName: "",
      ssn: "",
      dob: "",
      accountNumber: "",
      routingNumber: "",
      companyAddressLine1: "",
      companyAddressLine2: "",
      companyAddressCity: "",
      companyAddressState: "",
      companyAddressZip: "",
      accountTypeOptions: [
        {
          label: "Individual",
          value: "individual"
        },
        {
          label: "Company",
          value: "company"
        }
      ],
      stateOptions: states,
      errorMessage: "",
      mode: "create",
      individual: null,
      company: null,
      person: null
    }
  },
  async mounted() {
    const that = this

    if (this.$route.query.mode === "edit") {
      this.mode = "edit"
    }

    if (this.mode === "edit") {
      await this.$store.dispatch(ACCOUNT_GET)

      if (this.getCurrentAccount.stripe_account.account_info.business_type === "individual") {
        const individual = this.getCurrentAccount.stripe_account.account_info.individual
        this.individual = individual
        this.accountType = "individual"

        this.firstName = individual.first_name
        this.lastName = individual.last_name

        if (individual.ssn_last_4_provided) {
          this.ssn = "000000000"
        }

        if (individual.dob.day) {
          this.dob = "00000000"
        }
      } else {
        const company = this.getCurrentAccount.stripe_account.account_info.company
        const person = this.getCurrentAccount.stripe_account.account_info.persons[0]
        this.company = company
        this.person = person
        this.accountType = "company"

        this.businessName = company.name

        this.firstName = person.first_name
        this.lastName = person.last_name

        if (person.dob.day) {
          this.dob = "00000000"
        }

        if (person.ssn_last_4_provided) {
          this.ssn = "00000000"
        }

        if (person.ssn_last_4_provided) {
          this.ssn = "00000000"
        }

        if (company.tax_id_provided) {
          this.taxId = "00000000"
        }

        if (company.address) {
          this.companyAddressLine1 = company.address.line1
          this.companyAddressLine2 = company.address.line2
          this.companyAddressCity = company.address.city
          this.companyAddressState = company.address.state
          this.companyAddressZip = company.address.postal_code
        }
      }
    }

    hotkeys("ctrl+8", (event) => {
      event.preventDefault()

      that.accountType = "individual"
      that.firstName = "joe"
      that.lastName = "smith"
      that.ssn = "000000000"
      that.dob = "01/01/1990"
      that.accountNumber = "000123456789"
      that.routingNumber = "110000000"
      // that.companyAddressLine1 = "32 brook st"
      // that.companyAddressLine2 = "Unit 222"
      // that.companyAddressCity = "Patchogue"
      // that.companyAddressState = "NY"
      // that.companyAddressZip = "11772"
    })
  },
  methods: {
    handleEdit() {
      if (this.mode === "edit") {
        this.toggleEditModal()
        return
      }
    },
    toggleModal() {
      this.showModal = !this.showModal
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal
    },
    selectAccountType(selected) {
      this.accountType = selected.value
    },
    selectState(selected) {
      this.companyAddressState = selected.value
    },
    next() {
      if (!this.isValid) {
        this.errorMessage = "Please enter all required info"
        return
      }

      const accountInfo = {}

      accountInfo.mode = this.mode

      accountInfo.accountType = this.accountType
      accountInfo.firstName = this.firstName
      accountInfo.lastName = this.lastName
      accountInfo.ssn = this.ssn
      accountInfo.dob = this.dob
      accountInfo.accountNumber = this.accountNumber
      accountInfo.routingNumber = this.routingNumber

      if (this.accountType === "company") {
        accountInfo.businessName = this.businessName
        accountInfo.taxId = this.taxId
        accountInfo.companyAddressLine1 = this.companyAddressLine1
        accountInfo.companyAddressLine2 = this.companyAddressLine2
      }

      this.$store.commit(USER_SET_ACCOUNT_INFO, accountInfo)

      this.$router.push({
        name: "dashboard_my_payments_signup_pay"
      })
    }
  }
}
</script>
